import k from "../kaplayCtx";
import type { GameObj } from "kaplay";
import { getAuthManager } from "../../shared/systems/auth";
import {
  MENU_SCENE,
  INTRO_RIDER_SPEED,
  INTRO_RIDER_SCALE,
  INTRO_RIDER_Y,
  INTRO_BG_SPRITE,
  INTRO_FURNITURE_Y,
  INTRO_FURNITURE_SCALE,
  INTRO_TREE_SPRITE,
  INTRO_TREE_X,
  INTRO_CABINET_SPRITE,
  INTRO_CABINET_X,
  INTRO_SHOP_URL,
  INTRO_ARCADE_SPRITE,
  INTRO_ARCADE_X,
  INTRO_DOG_SPRITE,
  INTRO_DOG_X,
  INTRO_DOG_SCALE,
  INTRO_DOG_SPEED,
  INTRO_DOG_STOP_DISTANCE,
  INTRO_DOG_PET_DURATION,
  INTRO_REGISTER_SPRITE,
  INTRO_REGISTER_X,
  INTRO_REGISTER_PERSON_SPRITE,
  INTRO_REGISTER_PERSON_X,
  INTRO_REGISTER_PERSON_SCALE,
  INTRO_REPAIR_SPRITE,
  INTRO_REPAIR_X,
  INTRO_REPAIR_PHONE_URL,
  INTRO_REPAIR_PC_URL,
  INTRO_REPAIR_WORKER_SPRITE,
  INTRO_REPAIR_WORKER_X,
  INTRO_REPAIR_WORKER_SCALE,
  INTRO_SALES_WOMAN_SPRITE,
  INTRO_SALES_WOMAN_X,
  INTRO_SALES_WOMAN_SCALE,
  INTRO_EDUCATION_SPRITE,
  INTRO_EDUCATION_X,
  INTRO_EDUCATION_URL,
  INTRO_EDUCATION_WORKER_SPRITE,
  INTRO_EDUCATION_WORKER_X,
  INTRO_EDUCATION_WORKER_SCALE,
} from "../../biai-game/config/constants";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

type ModalGroup = Record<string, ReturnType<typeof k.add>>;

function openUrl(url: string): void {
  if (isMobile) {
    window.location.href = url;
  } else {
    window.open(url, "_blank");
  }
}

export default function intro() {
  const cx = k.center().x;
  const fy = INTRO_FURNITURE_Y;
  const fs = INTRO_FURNITURE_SCALE;

  // Background
  const introBg = k.add([
    k.sprite(INTRO_BG_SPRITE),
    k.pos(0, 0),
    k.scale(k.width() / 240, k.height() / 135),
  ]);

  // Zone labels (always visible)
  const labelY = fy - 500;
  const labelSize = 64;
  const labels = [];

  function makeLabel(text: string, x: number, y?: number) {
    const label = k.add([
      k.text(text, { font: "mania", size: labelSize }),
      k.anchor("center"),
      k.pos(x, y || labelY),
      k.color(0, 0, 0),
      k.z(200),
    ]);
    labels.push(label);
    return label;
  }

  function makeBoxedLabel(text: string, x: number, y: number) {
    const estW = text.length * labelSize * 0.45 + 40;
    const estH = labelSize + 20;
    const bg = k.add([
      k.rect(estW, estH, { radius: 8 }),
      k.anchor("center"),
      k.pos(x, y),
      k.color(0, 0, 0),
      k.z(199),
    ]);
    const txt = k.add([
      k.text(text, { font: "mania", size: labelSize }),
      k.anchor("center"),
      k.pos(x, y),
      k.color(255, 255, 255),
      k.z(200),
    ]);
    const group = [bg, txt];
    labels.push(bg, txt);
    return group;
  }

  const labelComprar = makeBoxedLabel("Comprar", INTRO_CABINET_X, labelY);
  const labelJugar = makeBoxedLabel("Jugar", INTRO_ARCADE_X, labelY);
  const labelRegistro = makeBoxedLabel("Registro", INTRO_REGISTER_X, labelY);
  const labelServicio = makeBoxedLabel("Servicio Técnico", INTRO_REPAIR_X, labelY);
  const labelEducacion = makeBoxedLabel("Educación", INTRO_EDUCATION_X, labelY);

  // Hand cursor on interactive elements
  const canvas = document.querySelector("canvas")!;
  function addHover(obj: GameObj<any>): void {
    obj.onHover(() => {
      canvas.style.cursor = "pointer";
    });
    obj.onHoverEnd(() => {
      canvas.style.cursor = "default";
    });
  }

  // Rider starts at center, anchor bot so feet align with furniture base
  const rider = k.add([
    k.sprite("rider"),
    k.pos(100, INTRO_RIDER_Y),
    k.scale(INTRO_RIDER_SCALE),
    k.anchor("bot"),
    k.z(100),
  ]);

  let targetX: number | null = null;
  let onArrival: (() => void) | null = null;
  let zoneClicked = false;


  rider.onUpdate(() => {
    if (targetX === null) return;

    const diff = targetX - rider.pos.x;
    if (Math.abs(diff) < 5) {
      rider.pos.x = targetX;
      rider.stop();
      targetX = null;
      if (onArrival) {
        const cb = onArrival;
        onArrival = null;
        cb();
      }
      return;
    }

    rider.move(INTRO_RIDER_SPEED * (diff > 0 ? 1 : -1), 0);
  });

  // Tree (first item, decorative)
  k.add([
    k.sprite(INTRO_TREE_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_TREE_X, fy),
    k.scale(fs),
    k.z(15),
  ]);

  // Shop zone — cabinet
  const cabinet = k.add([
    k.sprite(INTRO_CABINET_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_CABINET_X, fy),
    k.scale(fs),
    k.area(),
    k.z(10),
  ]);

  // Sales woman (in front of cabinet)
  const salesWoman = k.add([
    k.sprite(INTRO_SALES_WOMAN_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_SALES_WOMAN_X, fy),
    k.scale(INTRO_SALES_WOMAN_SCALE),
    k.area(),
    k.z(10),
  ]);
  salesWoman.play("idle");

  let shopModal: ModalGroup | null = null;

  function showShopModal() {
    if (shopModal) return;

    const cy = k.center().y;

    // Overlay background
    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    const border = k.add([
      k.rect(704, 204, { radius: 14 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(0, 0, 0),
      k.z(300),
    ]);

    const box = k.add([
      k.rect(700, 200, { radius: 12 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(301),
    ]);

    const btn = k.add([
      k.rect(600, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(30, 120, 230),
      k.area(),
      k.z(302),
    ]);

    const btnText = k.add([
      k.text("IR A LA TIENDA", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    shopModal = { overlay, box, border, btn, btnText };

    btn.onClick(() => {
      openUrl(INTRO_SHOP_URL);
      closeShopModal();
    });

    overlay.onClick(() => {
      closeShopModal();
    });
  }

  function closeShopModal() {
    if (!shopModal) return;
    for (const obj of Object.values(shopModal)) {
      k.destroy(obj);
    }
    shopModal = null;
  }

  const NPC_STOP_DISTANCE = 150;

  function walkToCabinet() {
    if (targetX !== null) return;
    zoneClicked = true;
    const offset = rider.pos.x < INTRO_CABINET_X ? -NPC_STOP_DISTANCE : NPC_STOP_DISTANCE;
    targetX = INTRO_CABINET_X + offset;
    onArrival = () => {
      showShopModal();
    };
    if (INTRO_CABINET_X < rider.pos.x) {
      rider.play("walk-left");
    } else {
      rider.play("walk-right");
    }
  }

  cabinet.onClick(() => { walkToCabinet(); });
  salesWoman.onClick(() => { walkToCabinet(); });

  // Arcade cabinet — opens menu/leaderboard then navigates to /biai-game
  const arcade = k.add([
    k.sprite(INTRO_ARCADE_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_ARCADE_X, fy - 40),
    k.scale(fs),
    k.area(),
    k.z(10),
  ]);

  let arcadeModal: ModalGroup | null = null;

  function showArcadeModal() {
    if (arcadeModal) return;

    const auth = getAuthManager();
    const loggedIn = auth.isAuthenticated();
    const cy = k.center().y;

    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    if (loggedIn) {
      const border = k.add([
        k.rect(704, 204, { radius: 14 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(0, 0, 0),
        k.z(300),
      ]);

      const box = k.add([
        k.rect(700, 200, { radius: 12 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(255, 255, 255),
        k.z(301),
      ]);

      const btn = k.add([
        k.rect(600, 100, { radius: 8 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(30, 120, 230),
        k.area(),
        k.z(302),
      ]);

      const btnText = k.add([
        k.text("JUGAR", { font: "mania", size: 56 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(255, 255, 255),
        k.z(303),
      ]);

      arcadeModal = { overlay, box, border, btn, btnText };

      btn.onClick(() => {
        closeArcadeModal();
        k.go(MENU_SCENE);
      });
    } else {
      const border = k.add([
        k.rect(704, 254, { radius: 14 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(0, 0, 0),
        k.z(300),
      ]);

      const box = k.add([
        k.rect(700, 250, { radius: 12 }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(255, 255, 255),
        k.z(301),
      ]);

      const msg = k.add([
        k.text("REGÍSTRATE O INICIA\nSESIÓN PARA JUGAR", {
          font: "mania",
          size: 56,
          align: "center",
        }),
        k.anchor("center"),
        k.pos(cx, cy),
        k.color(0, 0, 0),
        k.z(302),
      ]);

      arcadeModal = { overlay, box, border, msg };
    }

    overlay.onClick(() => {
      closeArcadeModal();
    });
  }

  function closeArcadeModal() {
    if (!arcadeModal) return;
    for (const obj of Object.values(arcadeModal)) {
      k.destroy(obj);
    }
    arcadeModal = null;
  }

  arcade.onClick(() => {
    if (targetX !== null) return;
    targetX = INTRO_ARCADE_X;
    onArrival = () => {
      showArcadeModal();
    };
    if (INTRO_ARCADE_X < rider.pos.x) {
      rider.play("walk-left");
    } else {
      rider.play("walk-right");
    }
  });

  // Dog — chases the player, click to pet
  const dog = k.add([
    k.sprite(INTRO_DOG_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_DOG_X, fy + 80),
    k.scale(INTRO_DOG_SCALE),
    k.area(),
    k.z(12),
  ]);
  dog.play("idle-right");

  let isPetting = false;
  dog.onClick(() => {
    if (isPetting) return;
    isPetting = true;
    zoneClicked = true;
    const anim = rider.pos.x < dog.pos.x ? "pet-left" : "pet-right";
    dog.play(anim);
    k.wait(INTRO_DOG_PET_DURATION, () => {
      isPetting = false;
    });
  });

  dog.onUpdate(() => {
    if (isPetting) return;

    const diff = rider.pos.x - dog.pos.x;
    const dist = Math.abs(diff);

    if (dist > INTRO_DOG_STOP_DISTANCE) {
      // Chase the player
      dog.move(INTRO_DOG_SPEED * (diff > 0 ? 1 : -1), 0);
      const walkAnim = diff > 0 ? "walk-right" : "walk-left";
      if (dog.curAnim() !== walkAnim) {
        dog.play(walkAnim);
      }
    } else {
      // Close enough, idle facing the player
      const idleAnim = diff > 0 ? "idle-right" : "idle-left";
      if (dog.curAnim() !== idleAnim) {
        dog.play(idleAnim);
      }
    }
  });

  // Register person (behind desk, always facing the player)
  const registerPerson = k.add([
    k.sprite(INTRO_REGISTER_PERSON_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_REGISTER_PERSON_X, fy),
    k.scale(INTRO_REGISTER_PERSON_SCALE),
    k.area(),
  ]);

  // Register desk (in front of person)
  const registerDesk = k.add([
    k.sprite(INTRO_REGISTER_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_REGISTER_X, fy),
    k.scale(fs),
    k.area(),
    k.z(10),
  ]);
  registerPerson.play("writing-left");

  let registerModal: ModalGroup | null = null;

  function showRegisterModal() {
    if (registerModal) return;

    const auth = getAuthManager();
    const loggedIn = auth.isAuthenticated();
    const cy = k.center().y;
    const activeColor: [number, number, number] = [30, 120, 230];
    const disabledColor: [number, number, number] = [160, 160, 160];

    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    const boxH = loggedIn ? 780 : 450;
    const border = k.add([
      k.rect(804, boxH + 4, { radius: 14 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(0, 0, 0),
      k.z(300),
    ]);

    const box = k.add([
      k.rect(800, boxH, { radius: 12 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(301),
    ]);

    const parts = [];

    if (loggedIn) {
      parts.push(k.add([
        k.text("BIENVENIDO!\n" + (auth.getUserName() ?? "").toUpperCase(), {
          font: "mania",
          size: 56,
          align: "center",
        }),
        k.anchor("center"),
        k.pos(cx, cy - 260),
        k.color(0, 0, 0),
        k.z(302),
      ]));
    }

    // LOGIN — disabled if logged in
    const btnLogin = k.add([
      k.rect(650, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy - (loggedIn ? 100 : 120)),
      k.color(...(loggedIn ? disabledColor : activeColor)),
      k.area(),
      k.z(302),
    ]);
    const btnLoginText = k.add([
      k.text("INICIAR", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy - (loggedIn ? 100 : 120)),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    // REGISTRO — disabled if logged in
    const btnRegister = k.add([
      k.rect(650, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy + (loggedIn ? 20 : 0)),
      k.color(...(loggedIn ? disabledColor : activeColor)),
      k.area(),
      k.z(302),
    ]);
    const btnRegisterText = k.add([
      k.text("CREAR CUENTA", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy + (loggedIn ? 20 : 0)),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    // LOGOUT — disabled if not logged in
    const btnLogout = k.add([
      k.rect(650, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy + (loggedIn ? 140 : 120)),
      k.color(...(loggedIn ? activeColor : disabledColor)),
      k.area(),
      k.z(302),
    ]);
    const btnLogoutText = k.add([
      k.text("SALIR", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy + (loggedIn ? 140 : 120)),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    // JUGAR — only when logged in
    let btnPlay = null;
    let btnPlayText = null;
    if (loggedIn) {
      btnPlay = k.add([
        k.rect(650, 100, { radius: 8 }),
        k.anchor("center"),
        k.pos(cx, cy + 260),
        k.color(30, 180, 60),
        k.area(),
        k.z(302),
      ]);
      btnPlayText = k.add([
        k.text("JUGAR", { font: "mania", size: 56 }),
        k.anchor("center"),
        k.pos(cx, cy + 260),
        k.color(255, 255, 255),
        k.z(303),
      ]);
    }

    registerModal = {
      overlay, box, border,
      ...Object.fromEntries(parts.map((p, i) => [`part${i}`, p])),
      btnLogin, btnLoginText,
      btnRegister, btnRegisterText,
      btnLogout, btnLogoutText,
      ...(btnPlay ? { btnPlay, btnPlayText } : {}),
    } as ModalGroup;

    if (btnPlay) {
      btnPlay.onClick(() => {
        closeRegisterModal();
        k.go(MENU_SCENE);
      });
    }

    btnLogin.onClick(async () => {
      if (loggedIn) return;
      await auth.login();
      closeRegisterModal();
      showRegisterModal();
    });

    btnRegister.onClick(async () => {
      if (loggedIn) return;
      await auth.login();
      closeRegisterModal();
      showRegisterModal();
    });

    btnLogout.onClick(async () => {
      if (!loggedIn) return;
      await auth.logout();
      closeRegisterModal();
      showRegisterModal();
    });

    overlay.onClick(() => {
      closeRegisterModal();
    });
  }

  function closeRegisterModal() {
    if (!registerModal) return;
    for (const obj of Object.values(registerModal)) {
      k.destroy(obj);
    }
    registerModal = null;
  }

  // Welcome modal (shown after redirect login on mobile)
  let welcomeModal: ModalGroup | null = null;

  function showWelcomeModal(userName: string): void {
    if (welcomeModal) return;

    const cy = k.center().y;

    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    const border = k.add([
      k.rect(704, 304, { radius: 14 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(0, 0, 0),
      k.z(300),
    ]);

    const box = k.add([
      k.rect(700, 300, { radius: 12 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(301),
    ]);

    const msg = k.add([
      k.text("BIENVENIDO!\n" + userName.toUpperCase(), {
        font: "mania",
        size: 56,
        align: "center",
      }),
      k.anchor("center"),
      k.pos(cx, cy - 50),
      k.color(0, 0, 0),
      k.z(302),
    ]);

    const btn = k.add([
      k.rect(600, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy + 70),
      k.color(30, 120, 230),
      k.area(),
      k.z(302),
    ]);

    const btnText = k.add([
      k.text("JUGAR", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy + 70),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    welcomeModal = { overlay, box, border, msg, btn, btnText };

    btn.onClick(() => {
      closeWelcomeModal();
      k.go(MENU_SCENE);
    });

    overlay.onClick(() => {
      closeWelcomeModal();
    });
  }

  function closeWelcomeModal() {
    if (!welcomeModal) return;
    for (const obj of Object.values(welcomeModal)) {
      k.destroy(obj);
    }
    welcomeModal = null;
  }

  function walkToRegister() {
    if (targetX !== null) return;
    zoneClicked = true;
    const offset = rider.pos.x < INTRO_REGISTER_X ? -NPC_STOP_DISTANCE : NPC_STOP_DISTANCE;
    targetX = INTRO_REGISTER_X + offset;
    onArrival = () => {
      showRegisterModal();
    };
    if (INTRO_REGISTER_X < rider.pos.x) {
      rider.play("walk-left");
    } else {
      rider.play("walk-right");
    }
  }

  registerDesk.onClick(() => { walkToRegister(); });
  registerPerson.onClick(() => { walkToRegister(); });

  let registerPersonDancing = false;
  registerPerson.onUpdate(() => {
    if (registerPersonDancing) return;
    const facingLeft = rider.pos.x < INTRO_REGISTER_PERSON_X;
    const anim = facingLeft ? "writing-left" : "writing-right";
    if (registerPerson.curAnim() !== anim) {
      registerPerson.play(anim);
    }
  });

  // Education worker (behind desk, typing)
  const educationWorker = k.add([
    k.sprite(INTRO_EDUCATION_WORKER_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_EDUCATION_WORKER_X, fy),
    k.scale(INTRO_EDUCATION_WORKER_SCALE),
    k.area(),
  ]);
  educationWorker.play("typing");

  // Education desk (in front of worker)
  const educationDesk = k.add([
    k.sprite(INTRO_EDUCATION_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_EDUCATION_X, fy),
    k.scale(fs),
    k.area(),
    k.z(10),
  ]);

  let educationModal: ModalGroup | null = null;

  function showEducationModal() {
    if (educationModal) return;

    const cy = k.center().y;

    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    const border = k.add([
      k.rect(704, 204, { radius: 14 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(0, 0, 0),
      k.z(300),
    ]);

    const box = k.add([
      k.rect(700, 200, { radius: 12 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(301),
    ]);

    const btn = k.add([
      k.rect(600, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(30, 120, 230),
      k.area(),
      k.z(302),
    ]);

    const btnText = k.add([
      k.text("IR A EDUCACIÓN", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    educationModal = { overlay, box, border, btn, btnText };

    btn.onClick(() => {
      openUrl(INTRO_EDUCATION_URL);
      closeEducationModal();
    });

    overlay.onClick(() => {
      closeEducationModal();
    });
  }

  function closeEducationModal() {
    if (!educationModal) return;
    for (const obj of Object.values(educationModal)) {
      k.destroy(obj);
    }
    educationModal = null;
  }

  function walkToEducation() {
    if (targetX !== null) return;
    zoneClicked = true;
    const offset = rider.pos.x < INTRO_EDUCATION_X ? -NPC_STOP_DISTANCE : NPC_STOP_DISTANCE;
    targetX = INTRO_EDUCATION_X + offset;
    onArrival = () => {
      showEducationModal();
    };
    if (INTRO_EDUCATION_X < rider.pos.x) {
      rider.play("walk-left");
    } else {
      rider.play("walk-right");
    }
  }

  educationDesk.onClick(() => { walkToEducation(); });
  educationWorker.onClick(() => { walkToEducation(); });

  let educationWorkerDancing = false;

  // Repair worker (behind station, welding sparks)
  const repairWorker = k.add([
    k.sprite(INTRO_REPAIR_WORKER_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_REPAIR_WORKER_X, fy),
    k.scale(INTRO_REPAIR_WORKER_SCALE),
    k.area(),
    k.z(20),
  ]);
  repairWorker.play("welding");

  // Repair station (in front of worker)
  const repairStation = k.add([
    k.sprite(INTRO_REPAIR_SPRITE),
    k.anchor("bot"),
    k.pos(INTRO_REPAIR_X, fy),
    k.scale(fs),
    k.area(),
    k.z(10),
  ]);

  let repairModal: ModalGroup | null = null;

  function showRepairModal() {
    if (repairModal) return;

    const cy = k.center().y;

    const overlay = k.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(0, 0, 0),
      k.opacity(0.6),
      k.area(),
      k.z(300),
    ]);

    const border = k.add([
      k.rect(704, 304, { radius: 14 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(0, 0, 0),
      k.z(300),
    ]);

    const box = k.add([
      k.rect(700, 300, { radius: 12 }),
      k.anchor("center"),
      k.pos(cx, cy),
      k.color(255, 255, 255),
      k.z(301),
    ]);

    const btnPhone = k.add([
      k.rect(600, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy - 60),
      k.color(30, 120, 230),
      k.area(),
      k.z(302),
    ]);

    const btnPhoneText = k.add([
      k.text("CELULAR", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy - 60),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    const btnPc = k.add([
      k.rect(600, 100, { radius: 8 }),
      k.anchor("center"),
      k.pos(cx, cy + 60),
      k.color(30, 120, 230),
      k.area(),
      k.z(302),
    ]);

    const btnPcText = k.add([
      k.text("PC", { font: "mania", size: 56 }),
      k.anchor("center"),
      k.pos(cx, cy + 60),
      k.color(255, 255, 255),
      k.z(303),
    ]);

    repairModal = { overlay, box, border, btnPhone, btnPhoneText, btnPc, btnPcText };

    btnPhone.onClick(() => {
      openUrl(INTRO_REPAIR_PHONE_URL);
      closeRepairModal();
    });

    btnPc.onClick(() => {
      openUrl(INTRO_REPAIR_PC_URL);
      closeRepairModal();
    });

    overlay.onClick(() => {
      closeRepairModal();
    });
  }

  function closeRepairModal() {
    if (!repairModal) return;
    for (const obj of Object.values(repairModal)) {
      k.destroy(obj);
    }
    repairModal = null;
  }

  function walkToRepair() {
    if (targetX !== null) return;
    zoneClicked = true;
    const offset = rider.pos.x < INTRO_REPAIR_X ? -NPC_STOP_DISTANCE : NPC_STOP_DISTANCE;
    targetX = INTRO_REPAIR_X + offset;
    onArrival = () => {
      showRepairModal();
    };
    if (INTRO_REPAIR_X < rider.pos.x) {
      rider.play("walk-left");
    } else {
      rider.play("walk-right");
    }
  }

  repairStation.onClick(() => { walkToRepair(); });
  repairWorker.onClick(() => { walkToRepair(); });

  // Close any open modal with Escape key
  k.onKeyPress("escape", () => {
    if (welcomeModal) { closeWelcomeModal(); return; }
    if (shopModal) { closeShopModal(); return; }
    if (arcadeModal) { closeArcadeModal(); return; }
    if (registerModal) { closeRegisterModal(); return; }
    if (educationModal) { closeEducationModal(); return; }
    if (repairModal) { closeRepairModal(); }
  });

  // Hover labels + hand cursor
  addHover(educationDesk);
  addHover(educationWorker);
  addHover(cabinet);
  addHover(salesWoman);
  addHover(arcade);
  addHover(registerDesk);
  addHover(registerPerson);
  addHover(repairStation);
  addHover(repairWorker);
  dog.onHover(() => { canvas.style.cursor = "pointer"; });
  dog.onHoverEnd(() => { canvas.style.cursor = "default"; });

  // Click anywhere else to freely move the rider
  educationDesk.onClick(() => { zoneClicked = true; });
  educationWorker.onClick(() => { zoneClicked = true; });
  cabinet.onClick(() => { zoneClicked = true; });
  salesWoman.onClick(() => { zoneClicked = true; });
  arcade.onClick(() => { zoneClicked = true; });
  registerDesk.onClick(() => { zoneClicked = true; });
  registerPerson.onClick(() => { zoneClicked = true; });
  repairStation.onClick(() => { zoneClicked = true; });
  repairWorker.onClick(() => { zoneClicked = true; });

  k.onClick(() => {
    const mouseX = k.mousePos().x;
    // Defer by one frame so object-specific handlers fire first on mobile
    k.wait(0, () => {
      if (zoneClicked) {
        zoneClicked = false;
        return;
      }
      targetX = mouseX;
      onArrival = null;
      if (mouseX > rider.pos.x) {
        rider.play("walk-right");
      } else {
        rider.play("walk-left");
      }
    });
  });

  // Show welcome modal if returning from redirect login (mobile)
  const auth = getAuthManager();
  if (auth.wasRedirectLogin()) {
    showWelcomeModal(auth.getUserName() ?? "");
  }
}
